import {
  CustomViewAttribute,
  CustomViewFormModel,
  CustomViewSorting,
} from 'src/graphql/entities/custom-view';
import { PartnerRaw } from 'src/types';
import { create, StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';

export type GridDensity = 'comfortable' | 'compact' | 'standard';

export interface CustomViewsStoreState {
  // state variables
  filtersMap: Record<string, CustomViewFormModel['conditions'] | null>;
  columnsMap: Record<string, CustomViewAttribute[] | null>;
  columnWidthMap: Record<string, number>;
  sortMap: Record<string, CustomViewSorting | null>;
  defaultPartnerFilter: PartnerRaw | null;
  gridDensity?: GridDensity;
  // setters
  updateFilters: (
    viewId: string,
    conditions: CustomViewFormModel['conditions'] | null,
  ) => void;
  updateColumns: (viewId: string, columns: CustomViewAttribute[] | null) => void;
  updateSort: (viewId: string, sort: CustomViewSorting | null) => void;
  updateColumnWidthMap: (columnIdentifier: string, width: number) => void;
  setDefaultPartnerFilter: (partner?: PartnerRaw | null) => void;
  setGridDensity: (density?: GridDensity) => void;
  // resetters
  resetFilters: () => void;
  resetColumns: () => void;
  resetSort: () => void;
}

export const createCustomViewsSlice: StateCreator<
  CustomViewsStoreState,
  [],
  [],
  CustomViewsStoreState
> = (set) => ({
  filtersMap: {},
  columnsMap: {},
  sortMap: {},
  columnWidthMap: {},
  defaultPartnerFilter: null,

  updateFilters: (viewId, conditions) =>
    set((state) => ({
      filtersMap: {
        ...state.filtersMap,
        [viewId]: conditions,
      },
    })),
  updateColumns: (viewId, columns) =>
    set((state) => ({
      columnsMap: {
        ...state.columnsMap,
        [viewId]: columns,
      },
    })),
  updateSort: (viewId, sort) =>
    set((state) => ({
      sortMap: {
        ...state.sortMap,
        [viewId]: sort,
      },
    })),
  updateColumnWidthMap: (columnIdentifier, width) =>
    set((state) => ({
      columnWidthMap: {
        ...state.columnWidthMap,
        [columnIdentifier]: width,
      },
    })),
  setDefaultPartnerFilter: (defaultPartnerFilter) => set({ defaultPartnerFilter }),
  setGridDensity: (gridDensity) => set({ gridDensity }),
  resetFilters: () => set({ filtersMap: {} }),
  resetColumns: () => set({ columnsMap: {} }),
  resetSort: () => set({ sortMap: {} }),
});

const useCustomViewsStore = create<CustomViewsStoreState>()(
  persist(
    (...args) => ({
      ...createCustomViewsSlice(...args),
    }),
    {
      name: 'portal-custom-view-store',
      partialize: (state) => {
        // exclude `defaultPartnerFilter` from being persisted
        const { defaultPartnerFilter, ...persistedState } = state;
        return persistedState;
      },
    },
  ),
);

export default useCustomViewsStore;
